import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import {
  FluentProvider,
  webLightTheme,
  Spinner,
  Toast,
  ToastTitle,
  Toaster,
  useToastController,
  ToastIntent,
  Theme,
} from '@fluentui/react-components';

import Dashboard from '@/pages/Dashboard';
import AuthCallback from '@/pages/AuthCallback';
import { useAppStore } from '@/stores/chatbotStore';
import './App.css';

// Symotronic / Alpilink corporate blue theme
const appTheme: Theme = {
  ...webLightTheme,
  // Brand colors — Alpilink blue palette
  colorBrandBackground: '#1A5FA8',
  colorBrandBackgroundHover: '#0B2D5E',
  colorBrandBackgroundPressed: '#0B2D5E',
  colorBrandForegroundLink: '#1A5FA8',
  colorBrandForegroundLinkHover: '#0B2D5E',
  // Neutral backgrounds
  colorNeutralBackground1: '#ffffff',
  colorNeutralBackground2: '#F2F4F7',
  colorNeutralBackground3: '#f3f4f6',
  // Subtle, clean shadows and borders
  colorNeutralStroke1: '#e5e7eb',
  colorNeutralStroke2: '#f3f4f6',
  // Typography
  fontFamilyBase: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  // Rounded corners
  borderRadiusMedium: '12px',
  borderRadiusLarge: '16px',
  borderRadiusXLarge: '24px',
};

const AppContent: React.FC = () => {
  const { error, chatError, uploadError, clearError } = useAppStore();
  const { dispatchToast } = useToastController('global-toaster');

  // Show toast notifications for errors
  useEffect(() => {
    if (error) {
      dispatchToast(
        <Toast>
          <ToastTitle>Erreur</ToastTitle>
          {error}
        </Toast>,
        { intent: 'error' as ToastIntent, timeout: 5000 }
      );
      clearError();
    }
  }, [error, dispatchToast, clearError]);

  useEffect(() => {
    if (chatError) {
      dispatchToast(
        <Toast>
          <ToastTitle>Erreur de Chat</ToastTitle>
          {chatError}
        </Toast>,
        { intent: 'error' as ToastIntent, timeout: 5000 }
      );
      clearError();
    }
  }, [chatError, dispatchToast, clearError]);

  useEffect(() => {
    if (uploadError) {
      dispatchToast(
        <Toast>
          <ToastTitle>Erreur d'Upload</ToastTitle>
          {uploadError}
        </Toast>,
        { intent: 'error' as ToastIntent, timeout: 5000 }
      );
      clearError();
    }
  }, [uploadError, dispatchToast, clearError]);

  return (
    <div className="app-container">
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster toasterId="global-toaster" />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <FluentProvider theme={appTheme}>
      <AppContent />
    </FluentProvider>
  );
};

export default App;